import { GroupData, GroupMode, Run } from '@bringg/types';
import { MappedStorableItem } from '@bringg/dashboard-sdk/dist/data-entity';
import { localLogger } from '@bringg-frontend/bringg-web-infra';

import { groupIdCreator } from './group-id-creator';
import { GroupUpdater } from './group-updater';
import { fieldValueGetter } from 'bringg-web/features/monitoring/shared/realtime-updates/updates/field-value-getter';
import { TableDefinition } from 'bringg-web/features/monitoring/shared/stores/groups/table-definition';
import { runsFilterer } from 'bringg-web/features/monitoring/shared/filters/runs-filterer';

function getGroupIds(
	{ current, diff }: MappedStorableItem<Run>,
	tableDefinition: TableDefinition
): { newGroupId: string; currentGroupId: string } {
	if (!isInRunGroupMode(tableDefinition.groupMode)) {
		return;
	}

	const isNewInFilter = current && isInFilter(current, tableDefinition);
	const isCurrentInFilter = diff && isInFilter(diff, tableDefinition, current);

	if (tableDefinition.groupMode === GroupMode.Route) {
		return { newGroupId: isNewInFilter ? '' : undefined, currentGroupId: isCurrentInFilter ? '' : undefined };
	}

	const newGroupId = isNewInFilter ? groupIdCreator.create(current, ['team_id']) : undefined;
	const currentGroupId = isCurrentInFilter ? groupIdCreator.create(diff, ['team_id'], current) : undefined;

	return { newGroupId, currentGroupId };
}

function isInFilter(run: Run, currentTableDefinition: TableDefinition, fallback?: Run) {
	return (
		runsFilterer.filter(currentTableDefinition.filters.run, run, fallback) &&
		runsFilterer.filter(currentTableDefinition.filters.task, run, fallback)
	);
}

function getGroupItems(group: GroupData, tableDefinition: TableDefinition): GroupData[] {
	return tableDefinition.groupMode === GroupMode.Route ? tableDefinition.groups : group?.groups;
}

function getSortValues({ current, diff }: MappedStorableItem<Run>) {
	const itemId = current ? current.id : diff.id;
	const startedAt = fieldValueGetter.get(diff, current, 'started_at');

	return {
		direction: 'asc' as const,
		values: [
			{
				field: startedAt ? 'started_at' : 'scheduled_start_time',
				value: startedAt || fieldValueGetter.get(diff, current, 'scheduled_start_time')
			},
			{ field: 'id', value: itemId }
		]
	};
}

function getSortFields() {
	return ['started_at', 'scheduled_start_time'];
}

function addItem(index: number, run: Run, groupId: string, group: GroupData, tableDefinition: TableDefinition) {
	if (groupId === undefined) {
		return;
	}

	const groupItems = getGroupItems(group, tableDefinition);

	if (groupItems) {
		const fullGroupId = groupId ? `${groupId}-${run.id}` : run.id.toString();
		localLogger.log(`realtime update: item - adding group [${fullGroupId}] in index [${index}]`);

		tableDefinition.addGroup(groupItems, {
			groupId: fullGroupId,
			groupType: 'route',
			data: {
				id: run.id,
				title: run.route_name || run.external_id,
				task_count: tableDefinition.itemsByGroup[fullGroupId]?.length || 0
			},
			index
		});
	}
}

function removeItem(group: GroupData, index: number, tableDefinition: TableDefinition) {
	const groupItems = getGroupItems(group, tableDefinition);
	const groupItem = groupItems[index];

	if (tableDefinition.itemsByGroup[groupItem?.id]) {
		const tasksCount = tableDefinition.itemsByGroup[groupItem.id].length;
		tableDefinition.totalTasks -= tasksCount;
		localLogger.log(
			`realtime update: group - [${groupItem.id}] - removing ${tasksCount} from total tasks - new total ${tableDefinition.totalTasks}`
		);

		const parts = groupItem.id.split('-');
		const parentGroup = parts.length > 1 ? tableDefinition.groupsMap[parts[0]] : undefined;

		if (parentGroup) {
			parentGroup.data.task_count -= tasksCount;
			localLogger.log(
				`realtime update: group - [${groupItem.id}] - removing ${tasksCount} from parent group ${parentGroup.id} - new total ${parentGroup.data.task_count}`
			);
		}
	}

	tableDefinition.removeGroup(groupItems, index);
}

function isInRunGroupMode(groupMode: GroupMode) {
	return [GroupMode.TeamRoute, GroupMode.Route].includes(groupMode);
}

export const runsGroupUpdater: GroupUpdater<Run, GroupData> = {
	getGroupIds,
	getGroupItems,
	getSortValues,
	getSortFields,
	addItem,
	removeItem
};
